import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { RichEditorToolbar } from "@/components/rich-editor/components/RichEditorToolbar.tsx";
import { EmojiImage } from "@/components/rich-editor/extensions/EmojiImage.tsx";
import { EmployeeMentions } from "@/components/rich-editor/extensions/EmployeeMentions.tsx";
import { ExtendedImage } from "@/components/rich-editor/extensions/ExtendedImage.tsx";
import { KeyboardShortcuts } from "@/components/rich-editor/extensions/KeyboardShortcuts.tsx";
import { Pdf } from "@/components/rich-editor/extensions/Pdf.tsx";
import { VariableHighlight } from "@/components/rich-editor/extensions/VariableHighlight.tsx";
import { Video } from "@/components/rich-editor/extensions/Video.tsx";
import { insertEmoji } from "@/components/rich-editor/rich-editor.helpers.ts";
import {
  PlaceholderOption,
  RichEditorBodyFileType,
} from "@/components/rich-editor/rich-editor.types.ts";
import { getImageSizeFromFile } from "@/helpers/utils.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiTemplate } from "@/types/template.types.ts";
import { Box, Button, FontAwesomeIcon, Text, textareaTheme } from "@skailer-com/uikit";
import { Link } from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import { TextAlign } from "@tiptap/extension-text-align";
import { BackgroundColor, Color, TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";

export interface RichEditorProps {
  value: string;
  onChange?: (value: string) => void;
  error?: string;
  placeholder?: string;
  placeholderOptions?: PlaceholderOption[];
  emailTemplates?: ApiTemplate[];
  onApplyTemplate?: (template: ApiTemplate) => void;
  readOnly?: boolean;
  highlightMentions?: boolean;
  hideToolbar?: boolean;
  likeTextArea?: boolean;
  label?: string;
  minHeight?: string | number;
  maxHeight?: string | number;
  uploadImage?: (file: File) => Promise<ApiFile | undefined>;
  uploadBodyFile?: (file: File, type: RichEditorBodyFileType) => Promise<ApiFile | undefined>;
  insertEmojiIsOn?: boolean;
  toolbarClassName?: string;
  onSave?: (editor: Editor) => void;
  hideHeadings?: boolean;
  hideLinks?: boolean;
}

export const RichEditor = forwardRef<Editor | null, RichEditorProps>(
  (
    {
      value,
      placeholder,
      onChange,
      error,
      emailTemplates,
      onApplyTemplate,
      placeholderOptions,
      readOnly = false,
      highlightMentions = true,
      hideToolbar = false,
      label,
      likeTextArea = false,
      minHeight,
      uploadImage,
      uploadBodyFile,
      maxHeight,
      toolbarClassName,
      insertEmojiIsOn = false,
      onSave,
      hideHeadings,
      hideLinks,
    },
    ref
  ) => {
    const [filled, setFilled] = useState<boolean>(value?.length > 0);
    const [focused, setFocused] = useState<boolean>(false);
    const { canIEdit } = usePermissions();

    const isScrollableReadOnly = readOnly && !!maxHeight;
    const variableIds = JSON.stringify(placeholderOptions?.map(({ id }) => id) ?? []);
    const variableIdsRef = useRef<readonly string[]>([]);

    const editor = useEditor(
      {
        content: value,
        extensions: [
          VariableHighlight(() => variableIdsRef.current),
          StarterKit.configure({
            underline: false,
            link: false,
          }),
          Underline,
          TextAlign.configure({ types: ["heading", "paragraph"] }),
          TextStyle,
          Color,
          BackgroundColor,
          Placeholder.configure({
            placeholder: placeholder || "",
          }),
          Link.configure({
            linkOnPaste: false,
            openOnClick: false,
          }),
          ExtendedImage.configure({
            HTMLAttributes: {
              class: "editor-image",
            },
          }),
          EmojiImage,
          ...(uploadBodyFile ? [Video, Pdf] : []),
          ...(highlightMentions && canIEdit(ApiPermission.see_employee_directory)
            ? [EmployeeMentions]
            : []),
          KeyboardShortcuts(onSave),
        ],
        editable: !readOnly,
        editorProps: {
          attributes: isScrollableReadOnly ? { tabindex: "0" } : {},
        },
        onUpdate({ editor }) {
          const text = editor.getText().trim();
          let hasMedia = false;

          editor.state.doc.descendants((node) => {
            if (["image", "emojiImage", "video", "pdf"].includes(node.type.name)) {
              hasMedia = true;
              return false;
            }
            return true;
          });

          const html = text === "" && !hasMedia ? "" : editor.getHTML();
          setFilled(html.length > 0);
          onChange?.(html);
        },
      },
      [readOnly, isScrollableReadOnly]
    );

    useEffect(() => {
      variableIdsRef.current = JSON.parse(variableIds) as string[];
      if (editor && !editor.isDestroyed) {
        editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
      }
    }, [editor, variableIds]);

    const insertAndUploadImage = useCallback(
      async (file: File) => {
        if (!uploadImage || !editor || !file) return;

        const { height, width } = await getImageSizeFromFile(file);
        const localUrl = URL.createObjectURL(file);
        const placeholderId = `temp-img-${Math.random().toString(36).substring(2, 15)}`;

        editor.commands.insertContent({
          type: "image",
          attrs: {
            placeholderId,
            src: localUrl,
            style: `aspect-ratio: ${width} / ${height}; width: ${width}px; opacity: 0.2;`,
          },
        });

        const fileLoaded = await uploadImage(file);
        const state = editor.state;
        const { tr } = state;

        URL.revokeObjectURL(localUrl);

        let placeholderPos: number | null = null;
        state.doc.descendants((node, pos) => {
          if (node.type.name === "image" && node.attrs.placeholderId === placeholderId) {
            placeholderPos = pos;
            return false;
          }
          return true;
        });

        if (placeholderPos !== null) {
          const imageNode = state.schema.nodes.image.create({
            src: fileLoaded?.url_permanent,
            alt: "Image",
            style: `aspect-ratio: ${width} / ${height}; width: ${width}px`,
          });

          editor.view.dispatch(tr.replaceWith(placeholderPos, placeholderPos + 1, imageNode));
        }
      },
      [editor, uploadImage]
    );

    const insertAndUploadBodyFile = useCallback(
      async (file: File, type: RichEditorBodyFileType) => {
        if (!uploadBodyFile || !editor) return;

        const uploaded = await uploadBodyFile(file, type);
        if (!uploaded || editor.isDestroyed) return;

        editor
          .chain()
          .focus()
          .insertContent({ type, attrs: { fileUuid: uploaded.uuid, fileName: uploaded.name } })
          .run();
      },
      [editor, uploadBodyFile]
    );

    useImperativeHandle(ref, () => editor as Editor, [editor]);

    useEffect(() => {
      if (!editor) return;

      if (value === "") {
        editor.commands.clearContent();
        setFilled(false);
      } else if (value !== editor.getHTML()) {
        editor.commands.setContent(value);
        setFilled(true);
      }

      const handleFocus = () => setFocused(true);
      const handleBlur = () => setFocused(false);

      const handlePaste = ({ event }: { event: ClipboardEvent }) => {
        const items = event.clipboardData?.items;
        if (!items || !items.length || readOnly || !uploadImage) return;

        for (const item of Array.from(items)) {
          if (!item.type.startsWith("image")) continue;

          const file = item.getAsFile();
          if (!file) continue;

          event.preventDefault();
          insertAndUploadImage(file);
        }
      };

      editor.on("focus", handleFocus);
      editor.on("blur", handleBlur);
      editor.on("paste", handlePaste);

      return () => {
        editor.off("focus", handleFocus);
        editor.off("blur", handleBlur);
        editor.off("paste", handlePaste);
      };
    }, [value, editor, readOnly, uploadImage, insertAndUploadImage]);

    if (!editor) {
      return null;
    }

    const editorStyles: CSSProperties = {
      "--editor-min-height": minHeight
        ? typeof minHeight === "number"
          ? `${minHeight}px`
          : minHeight
        : 0,
      "--editor-max-height": maxHeight
        ? typeof maxHeight === "number"
          ? `${maxHeight}px`
          : maxHeight
        : "auto",
    } as CSSProperties;

    const showInlineEmoji = !readOnly && hideToolbar && insertEmojiIsOn && likeTextArea;

    return (
      <Box>
        <Box
          className={
            likeTextArea ? "border-b border-dark/10 relative" : "border border-dark/5 rounded-t-xs"
          }
          style={editorStyles}
        >
          {label && (
            <div
              className={twMerge(
                "absolute transition-all text-dark/70 select-none pointer-events-none",
                textareaTheme.label.base,
                filled || focused ? textareaTheme.label.focused : textareaTheme.label.usual,
                readOnly && textareaTheme.label.readonly
              )}
            >
              {label}
            </div>
          )}
          {!readOnly && !hideToolbar && (
            <RichEditorToolbar
              editor={editor}
              emailTemplates={emailTemplates}
              placeholderOptions={placeholderOptions}
              onApplyTemplate={onApplyTemplate}
              insertAndUploadImage={uploadImage ? insertAndUploadImage : undefined}
              insertAndUploadBodyFile={uploadBodyFile ? insertAndUploadBodyFile : undefined}
              toolbarClassName={toolbarClassName}
              insertEmojiIsOn={insertEmojiIsOn}
              hideHeadings={hideHeadings}
              hideLinks={hideLinks}
            />
          )}
          <EditorContent
            editor={editor}
            className={twMerge(
              likeTextArea ? "pb-2 pt-6 rich-editor" : "p-4 rich-editor",
              showInlineEmoji && "pr-8"
            )}
          />
          {showInlineEmoji && (
            <Box className={"absolute bottom-1 right-0"}>
              <EmojiPickerDropdown onEmojiSelect={(emoji) => insertEmoji(editor, emoji)}>
                <Button
                  variant={"secondary"}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-face-smile-plus" />}
                ></Button>
              </EmojiPickerDropdown>
            </Box>
          )}
        </Box>

        {!readOnly && error && (
          <Box className={"pt-2 pb-4"}>
            <Text className="text-danger">{error}</Text>
          </Box>
        )}
      </Box>
    );
  }
);
